"use client";

// The face of a post — its own image when it has one that loads, otherwise the
// generated card.
//
// Both are just an <img>. The generated card used to be rebuilt in CSS at
// every call site, which meant its layout had to survive whatever box it was
// dropped into; in the list view it didn't, and the type and the creature came
// apart. It is a hosted PNG now, composed once from the shared composition and
// served from storage, so every surface shows the same picture at whatever size
// it needs.
//
// STORAGE FIRST, THE ROUTE ON A MISS. The hosted file's address is a pure
// function of the post (lib/company/postCardUrl), so the browser asks the CDN
// for it directly. Only when that 404s — the card has not been composed yet —
// does the <img> swap to the composing route, which answers with the bytes and
// hosts them for everyone after. Before this every thumbnail woke a function
// that HEAD-probed storage and answered 302: a second per card, on every visit,
// for a file that already existed.
//
// The fallback still matters the other way too: several posts point at files
// that were never deployed (/media/shot-activity.jpg), and a card showing raw
// alt text is worse than a generated one. A failed load swaps to the card, so
// the next dead link heals itself rather than waiting to be noticed.

import { useState } from "react";
import MediaThumb, { thumbSrc } from "./MediaThumb";
import { hostedCardUrl, postCardUrl, realMedia, type CardAuthor } from "../../lib/company/postCardUrl";
import type { CompanyPost } from "../../lib/company/types";

export default function PostFace({
  post,
  author,
}: {
  post: CompanyPost;
  author: CardAuthor | null;
  /** kept for call-site compatibility; the hosted card needs no size variant */
  className?: string;
}) {
  const [broken, setBroken] = useState(false);
  // a post's own picture — never its own generated card filed as media
  const media = realMedia(post)[0];
  const src = media ? thumbSrc(media) : null;

  if (media && src && !broken) {
    return (
      <>
        <MediaThumb m={media} />
        {/* a hidden probe on the same url, so a dead image swaps the whole card */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt="" aria-hidden style={{ display: "none" }} onError={() => setBroken(true)} />
      </>
    );
  }

  return <GeneratedCard post={post} author={author} />;
}

function GeneratedCard({ post, author }: { post: CompanyPost; author: CardAuthor | null }) {
  const route = postCardUrl(post, author);
  const hosted = hostedCardUrl(post);
  const [src, setSrc] = useState(hosted ?? route);
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className="co-postcard"
      src={src}
      alt={post.title}
      loading="lazy"
      // not hosted yet → the route composes it, answers with the bytes, and
      // hosts it for the next visitor. Once only: the route is the last stop.
      onError={() => { if (src !== route) setSrc(route); }}
    />
  );
}
